/**
 * Service Feature Map
 *
 * Maps UI feature flags to their relevant control plane services.
 * This enables context-aware health warnings - only show warnings
 * for services that are actually relevant to the current operation.
 *
 * @example
 * When creating a VM (hypervisor=true, imageLibrary=true, blockStorage=true):
 * - Check: compute, placement, instance-ha, hamgr, infra-optim, mors
 * - Check: image (public), image (admin), image-cluster
 * - Check: volumev3, key-manager
 * - Check: identity, resmgr (always)
 *
 * When creating a Network (network=true):
 * - Check: network, load-balancer, dns
 * - Check: identity, resmgr (always)
 */

import { ServiceNameMap } from './useControlPlaneServicesHealth'

export type ServiceFeature =
  | 'hypervisor'
  | 'imageLibrary'
  | 'blockStorage'
  | 'network'
  | 'imageLibraryPublic'

export interface ServiceFeatureFlags {
  hypervisor?: boolean
  imageLibrary?: boolean
  // Restricts the imageLibrary check to one glance endpoint, e.g. image upload via
  // URL only hits the public endpoint, so the admin endpoint is irrelevant there.
  imageLibraryEndpoint?: 'public' | 'admin'
  blockStorage?: boolean
  network?: boolean
}

export const SERVICE_FEATURE_MAP: Record<ServiceFeature | 'core', string[]> = {
  // Services relevant to Hypervisor/VM/Compute operations
  hypervisor: [
    'compute', // Nova - VM lifecycle
    'placement', // Placement API - resource allocation
    'instance-ha', // VM Evacuation Service
    'hamgr', // VM High Availability Manager
    'infra-optim', // Dynamic Resource Rebalancing (Watcher)
    'mors', // Lease Manager (GPU leasing)
  ],

  // Services relevant to Image Library operations
  imageLibrary: [
    'image (public)', // Glance public endpoint
    'image (admin)', // Glance admin endpoint
    'image-cluster', // Glance cluster service
  ],

  imageLibraryPublic: [
    'image (public)', // Glance public endpoint
  ],

  // Services relevant to Block Storage/Volume operations
  blockStorage: [
    'volumev3', // Cinder - Volume management
    'key-manager', // Barbican - Encryption keys for volumes
  ],

  // Services relevant to Networking operations
  network: [
    'network', // Neutron - Network management
    'load-balancer', // Octavia - Load balancers
    'dns', // Designate - DNS management
  ],

  // Core services that affect ALL operations (always checked)
  core: [
    'identity', // Keystone - Authentication/Authorization
    'resmgr', // Resource Manager - Host management
  ],
}

/**
 * Get all relevant service types based on feature flags
 *
 * @param features - Object with feature flags
 * @returns Array of unique service type strings
 *
 * @example
 * getRelevantServiceTypes({ hypervisor: true, network: false })
 * // Returns: ['identity', 'resmgr', 'compute', 'placement', ...]
 */
export const getRelevantServiceTypes = (features: ServiceFeatureFlags): string[] => {
  const types: string[] = [...SERVICE_FEATURE_MAP.core] // Always include core

  if (features.hypervisor) {
    types.push(...SERVICE_FEATURE_MAP.hypervisor)
  }
  if (features.imageLibrary) {
    if (features.imageLibraryEndpoint === 'public') {
      types.push(...SERVICE_FEATURE_MAP.imageLibraryPublic)
    } else {
      types.push(...SERVICE_FEATURE_MAP.imageLibrary)
    }
  }
  if (features.blockStorage) {
    types.push(...SERVICE_FEATURE_MAP.blockStorage)
  }
  if (features.network) {
    types.push(...SERVICE_FEATURE_MAP.network)
  }

  return [...new Set(types)] // Remove duplicates
}

/**
 * Check if a service type is relevant to the given feature flags
 *
 * @param serviceType - The service type to check
 * @param features - Object with feature flags
 * @returns boolean indicating if the service is relevant
 */
export const isServiceRelevant = (serviceType: string, features: ServiceFeatureFlags): boolean => {
  const relevantTypes = getRelevantServiceTypes(features)
  return relevantTypes.includes(serviceType)
}

/**
 * Get display name for a service type
 * Reuses ServiceNameMap from useControlPlaneServicesHealth to avoid duplication
 *
 * @param serviceType - The service type
 * @returns Human-readable display name
 */
export const getServiceDisplayName = (serviceType: string): string => {
  return ServiceNameMap[serviceType] || serviceType
}
